import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { httpClient } from '../../services/httpClient';
import { categoryService } from '../../services/categoryService';
import { typeService } from '../../services/typeService';
import { sourceService } from '../../services/sourceService';
import { tagService } from '../../services/tagService';
import { groupService } from '../../services/groupService';
import { userService } from '../../services/userService';
import toast from 'react-hot-toast';
import ChannelGroupAccessConfig from '../omnichannel/ChannelGroupAccessConfig';

interface EmailChannelConfig {
  channelId: string;
  inboundAddress: string;
  defaultSettings: {
    priority?: string;
    categoryId?: number;
    typeId?: number;
    sourceId?: number;
    tagIds?: number[];
    assignedGroupId?: number;
    assignedToId?: number | 'any';
  };
}

interface Category {
  id: number;
  name: string;
}

interface Type {
  id: number;
  name: string;
}

interface Source {
  id: number;
  name: string;
}

interface Tag {
  id: number;
  name: string;
}

interface Group {
  id: number;
  name: string;
}

interface User {
  id: number;
  full_name: string;
  email: string;
}

export default function EmailSettings() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<EmailChannelConfig | null>(null);

  // Dropdown options
  const [categories, setCategories] = useState<Category[]>([]);
  const [types, setTypes] = useState<Type[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [agents, setAgents] = useState<User[]>([]);

  const priorityOptions = [
    { value: 'low', label: t('priority.low', 'Low') },
    { value: 'medium', label: t('priority.medium', 'Medium') },
    { value: 'high', label: t('priority.high', 'High') },
    { value: 'urgent', label: t('priority.urgent', 'Urgent') }
  ];

  useEffect(() => {
    loadEmailConfig();
    loadDropdownData();
  }, []);

  const loadEmailConfig = async () => {
    try {
      const response = await httpClient.get<{ success: boolean; data: EmailChannelConfig }>(
        '/api/channels/email/config'
      );
      setConfig(response.data);
    } catch (error: any) {
      console.error('Failed to load email config:', error);
      toast.error(t('settings.emailSettings.loadError', 'Failed to load email configuration'));
    } finally {
      setLoading(false);
    }
  };

  const loadDropdownData = async () => {
    try {
      const [categoriesRes, typesRes, sourcesRes, tagsRes, groupsRes, agentsRes] =
        await Promise.all([
          categoryService.getAllCategories(),
          typeService.getAllTypes(),
          sourceService.getAllSources(),
          tagService.getAllTags(),
          groupService.getAllGroups(),
          userService.getUsers()
        ]);

      setCategories(categoriesRes || []);
      setTypes(typesRes || []);
      setSources(sourcesRes || []);
      setTags(tagsRes || []);
      setGroups(groupsRes || []);
      setAgents(agentsRes || []);
    } catch (error) {
      console.error('Failed to load dropdown data:', error);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    setSaving(true);
    try {
      await httpClient.put('/api/channels/email/config', {
        defaultSettings: config.defaultSettings
      });
      toast.success(t('settings.emailSettings.saveSuccess', 'Email settings saved successfully'));
    } catch (error: any) {
      console.error('Failed to save email config:', error);
      toast.error(t('settings.emailSettings.saveError', 'Failed to save email settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyAddress = () => {
    if (config?.inboundAddress) {
      navigator.clipboard.writeText(config.inboundAddress);
      toast.success(t('settings.emailSettings.addressCopied', 'Email address copied to clipboard'));
    }
  };

  const updateSetting = (key: keyof EmailChannelConfig['defaultSettings'], value: any) => {
    if (!config) return;
    setConfig({
      ...config,
      defaultSettings: {
        ...config.defaultSettings,
        [key]: value
      }
    });
  };

  const toggleTag = (tagId: number) => {
    if (!config) return;
    const currentTags = config.defaultSettings.tagIds || [];
    const newTags = currentTags.includes(tagId)
      ? currentTags.filter((id) => id !== tagId)
      : [...currentTags, tagId];
    updateSetting('tagIds', newTags);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div
          className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
            theme === 'dark' ? 'border-gray-300' : 'border-gray-900'
          }`}
        ></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
        <p>{t('settings.emailSettings.noChannel', 'No email channel configured')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {t('settings.emailSettings.title', 'Email Settings')}
        </h2>
        <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {t(
            'settings.emailSettings.description',
            'Configure email channel settings and default ticket values'
          )}
        </p>
      </div>

      {/* Receiving Email Address */}
      <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
        <label
          className={`block text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
          }`}
        >
          {t('settings.emailSettings.receivingAddress', 'Customer Support Email Address')}
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={config.inboundAddress}
            readOnly
            className={`flex-1 rounded-md px-3 py-2 ${
              theme === 'dark'
                ? 'bg-gray-800 text-gray-200 border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            } border`}
          />
          <button
            onClick={handleCopyAddress}
            className={`px-4 py-2 rounded-md font-medium ${
              theme === 'dark'
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
          >
            {t('common.copy', 'Copy')}
          </button>
        </div>
        <p className={`mt-2 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          {t(
            'settings.emailSettings.addressHelp',
            'Share this email with customers or forward your support emails here'
          )}
        </p>
      </div>

      {/* Default Ticket Settings */}
      <div className="space-y-4">
        <h3
          className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
        >
          {t('settings.emailSettings.defaultSettings', 'Default Ticket Settings')}
        </h3>
        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          {t(
            'settings.emailSettings.defaultSettingsHelp',
            'These values will be applied to tickets created from incoming emails'
          )}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Priority */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}
            >
              {t('settings.emailSettings.defaultPriority', 'Default Priority')}
            </label>
            <select
              value={config.defaultSettings.priority || ''}
              onChange={(e) => updateSetting('priority', e.target.value || undefined)}
              className={`w-full rounded-md px-3 py-2 ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 border-gray-600'
                  : 'bg-white text-gray-900 border-gray-300'
              } border`}
            >
              <option value="">{t('common.none', 'None')}</option>
              {priorityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}
            >
              {t('settings.emailSettings.defaultCategory', 'Default Category')}
            </label>
            <select
              value={config.defaultSettings.categoryId || ''}
              onChange={(e) =>
                updateSetting('categoryId', e.target.value ? parseInt(e.target.value) : undefined)
              }
              className={`w-full rounded-md px-3 py-2 ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 border-gray-600'
                  : 'bg-white text-gray-900 border-gray-300'
              } border`}
            >
              <option value="">{t('common.none', 'None')}</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Type */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}
            >
              {t('settings.emailSettings.defaultType', 'Default Type')}
            </label>
            <select
              value={config.defaultSettings.typeId || ''}
              onChange={(e) =>
                updateSetting('typeId', e.target.value ? parseInt(e.target.value) : undefined)
              }
              className={`w-full rounded-md px-3 py-2 ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 border-gray-600'
                  : 'bg-white text-gray-900 border-gray-300'
              } border`}
            >
              <option value="">{t('common.none', 'None')}</option>
              {types.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {/* Source */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}
            >
              {t('settings.emailSettings.defaultSource', 'Default Source')}
            </label>
            <select
              value={config.defaultSettings.sourceId || ''}
              onChange={(e) =>
                updateSetting('sourceId', e.target.value ? parseInt(e.target.value) : undefined)
              }
              className={`w-full rounded-md px-3 py-2 ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 border-gray-600'
                  : 'bg-white text-gray-900 border-gray-300'
              } border`}
            >
              <option value="">{t('common.none', 'None')}</option>
              {sources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name}
                </option>
              ))}
            </select>
          </div>

          {/* Assign Group */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}
            >
              {t('settings.emailSettings.defaultGroup', 'Assign to Group')}
            </label>
            <select
              value={config.defaultSettings.assignedGroupId || ''}
              onChange={(e) =>
                updateSetting(
                  'assignedGroupId',
                  e.target.value ? parseInt(e.target.value) : undefined
                )
              }
              className={`w-full rounded-md px-3 py-2 ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 border-gray-600'
                  : 'bg-white text-gray-900 border-gray-300'
              } border`}
            >
              <option value="">{t('common.none', 'None')}</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Assign Agent */}
          <div>
            <label
              className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}
            >
              {t('settings.emailSettings.defaultAgent', 'Assign to Agent')}
            </label>
            <select
              value={config.defaultSettings.assignedToId || ''}
              onChange={(e) => {
                const val = e.target.value;
                updateSetting(
                  'assignedToId',
                  val === 'any' ? 'any' : val ? parseInt(val) : undefined
                );
              }}
              className={`w-full rounded-md px-3 py-2 ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 border-gray-600'
                  : 'bg-white text-gray-900 border-gray-300'
              } border`}
            >
              <option value="">{t('common.none', 'None')}</option>
              <option value="any">{t('settings.emailSettings.anyAgent', 'Any Agent')}</option>
              {agents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.full_name} ({agent.email})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tags */}
        <div>
          <label
            className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}
          >
            {t('settings.emailSettings.defaultTags', 'Default Tags')}
          </label>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => toggleTag(tag.id)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  (config.defaultSettings.tagIds || []).includes(tag.id)
                    ? theme === 'dark'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-indigo-600 text-white'
                    : theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Channel Group Access Configuration */}
      {config.channelId && (
        <ChannelGroupAccessConfig channelId={config.channelId} channelName="Email Channel" />
      )}

      {/* Save Button */}
      <div className="flex justify-end pt-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-6 py-2 rounded-md font-medium ${
            saving
              ? theme === 'dark'
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : theme === 'dark'
                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          {saving ? t('common.saving', 'Saving...') : t('common.save', 'Save Settings')}
        </button>
      </div>
    </div>
  );
}
